const _excluded = ["style", "className", "options", "defaultValue", "value", "width", "onChange", "onClose", "renderItem", "safePolygonArgs"],
  _excluded2 = ["key"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Divider from '@adsk/alloy-react-divider';
import { optionalPropType, useControlled, warn } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import { stylePropType } from '@adsk/alloy-react-theme';
import { getOptionsDuplicatedKeys } from './helpers/helpers';
import { menuItemShape, menuCategoryShape } from './helpers/propTypes';
import MenuCategory from './layout/MenuCategory';
import MenuList from './layout/MenuList';
import OverlayMenuItem from './layout/OverlayMenuItem';
/** Menu is intended to be a component that build a menu out of other components */
const MenuWrapped = (_ref, ref) => {
  let {
      style,
      className,
      options,
      defaultValue = [],
      value,
      width = 300,
      onChange,
      onClose,
      renderItem,
      safePolygonArgs
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [selectedItems, setSelectedItems] = useControlled(defaultValue, value, onChange);
  const optionsWithCategory = useMemo(() => {
    var _options$;
    if (!options) return [];
    if (options && (_options$ = options[0]) !== null && _options$ !== void 0 && _options$.hasOwnProperty('itemOptions')) {
      return options;
    }
    return [{
      key: typeof options[0].key === 'string' ? 'single-menu' : 0,
      allowReselect: true,
      showSelection: false,
      multiSelection: false,
      itemOptions: options
    }];
  }, [options]);
  useEffect(() => {
    const duplicates = getOptionsDuplicatedKeys(optionsWithCategory);
    if ((duplicates === null || duplicates === void 0 ? void 0 : duplicates.length) > 0) {
      warn("[Menu]: Options has Duplicate keys: ".concat(duplicates.join(', '), ", please use unique menu item key among all catagories"));
    }
  }, [optionsWithCategory]);
  return /*#__PURE__*/React.createElement(Overlay.Container, _extends({
    ref: ref,
    className: className,
    style: [{
      width
    }, style],
    variant: Overlay.VARIANTS.LIGHT
  }, props), optionsWithCategory.map((category, index) => /*#__PURE__*/React.createElement(MenuCategory, {
    key: category.key,
    title: category.title
  }, /*#__PURE__*/React.createElement(MenuList, null, category.itemOptions.map(_ref2 => {
    let {
        key
      } = _ref2,
      rest = _objectWithoutProperties(_ref2, _excluded2);
    return /*#__PURE__*/React.createElement(OverlayMenuItem, _extends({
      key: key,
      itemKey: key,
      renderItem: renderItem,
      category: category,
      defaultValue: defaultValue,
      value: selectedItems,
      onChange: setSelectedItems,
      onClose: onClose,
      safePolygonArgs: safePolygonArgs
    }, rest));
  })), (options === null || options === void 0 ? void 0 : options.length) - 1 !== index && /*#__PURE__*/React.createElement(Divider, null))));
};
const Menu = /*#__PURE__*/React.forwardRef(MenuWrapped);
Menu.displayName = 'Menu';
Menu.propTypes = _objectSpread(_objectSpread({}, Overlay.Container.propTypes), {}, {
  /* Styles applied to the root element */
  style: stylePropType,
  /* Class applied to the root element */
  className: PropTypes.string,
  /** Options for the action */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options: PropTypes.arrayOf(PropTypes.oneOfType([menuItemShape, menuCategoryShape])).isRequired,
  /** Pre selected keys */
  defaultValue: optionalPropType(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired)),
  /** Value for controlled usage */
  value: optionalPropType(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired)),
  /** Handle for item click event */
  onChange: PropTypes.func,
  /** Handle closing of the menu */
  onClose: PropTypes.func,
  /** Handle clicking a menu item */
  onClick: PropTypes.func,
  /** the width of the menu container  */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** render custom menu item */
  renderItem: PropTypes.func,
  /**
   * Options that modify how the safe polygon for nested menus behaves (see floating-ui documentation https://floating-ui.com/docs/useHover#safepolygon).
   * Also includes an enabled flag to disable the safe polygon for nested menus.
   */
  safePolygonArgs: PropTypes.shape({
    enabled: PropTypes.bool,
    buffer: optionalPropType(PropTypes.number),
    blockPointerEvents: optionalPropType(PropTypes.bool),
    requireIntent: optionalPropType(PropTypes.bool)
  })
});
export default Menu;
//# sourceMappingURL=Menu.js.map